/**
 * Configurable Table Badge Component
 * A customizable table that can be overlaid on plant images with configurable columns
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Typography,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// Types
interface TableColumn {
  id: string;
  label: string;
  type: 'tag_name' | 'value' | 'unit' | 'alarm_status' | 'description' | 'quality' | 'custom';
  width?: number;
  visible: boolean;
  format?: 'number' | 'boolean' | 'text' | 'timestamp';
  decimal_places?: number;
  custom_expression?: string; // For custom columns
}

interface TableTag {
  id: number;
  tag_name: string;
  description?: string;
  current_value: number | string | boolean | null;
  unit?: string;
  quality: 'GOOD' | 'BAD' | 'UNCERTAIN';
  alarm_definitions?: AlarmDefinition[];
  last_updated?: string;
}

interface AlarmDefinition {
  id: number;
  name: string;
  alarm_type: string;
  alarm_priority: {
    name: string;
    color: string;
    level: number;
  };
  is_active?: boolean;
  current_status?: 'ACTIVE' | 'ACKNOWLEDGED' | 'CLEARED';
}

interface ConfigurableTableBadgeProps {
  title: string;
  tags: TableTag[];
  columns: TableColumn[];
  maxHeight?: number;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  showRowNumbers?: boolean;
  alternateRowColors?: boolean;
  headerColor?: string;
  size?: 'small' | 'medium';
  onTagClick?: (tag: TableTag) => void;
  onAlarmClick?: (alarm: AlarmDefinition) => void;
}

const ConfigurableTableBadge: React.FC<ConfigurableTableBadgeProps> = ({
  title,
  tags,
  columns,
  maxHeight = 400,
  collapsible = true,
  defaultExpanded = true,
  showRowNumbers = false,
  alternateRowColors = true,
  headerColor = '#1976d2',
  size = 'small',
  onTagClick,
  onAlarmClick,
}) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  
  // Filter visible columns
  const visibleColumns = columns.filter(col => col.visible);
  
  // Format value based on column configuration
  const formatValue = (value: any, column: TableColumn, tag: TableTag): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>--</span>;
    }
    
    switch (column.type) {
      case 'tag_name':
        return (
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 'medium',
              cursor: onTagClick ? 'pointer' : 'default',
              '&:hover': onTagClick ? { textDecoration: 'underline' } : {}
            }}
            onClick={() => onTagClick?.(tag)}
          >
            {tag.tag_name}
          </Typography>
        );
        
      case 'value':
        let formattedValue = value;
        if (column.format === 'number' && typeof value === 'number') {
          formattedValue = value.toFixed(column.decimal_places || 2);
        } else if (column.format === 'boolean') {
          return (
            <Chip
              label={value ? 'True' : 'False'}
              color={value ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {formattedValue}
            </Typography>
            {tag.unit && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {tag.unit}
              </Typography>
            )}
          </Box>
        );
        
      case 'unit':
        return (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {tag.unit || '--'}
          </Typography>
        );
        
      case 'alarm_status':
        return renderAlarmStatus(tag.alarm_definitions || [], onAlarmClick);
        
      case 'description':
        return (
          <Tooltip title={tag.description || ''} arrow>
            <Typography 
              variant="body2" 
              sx={{ 
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {tag.description || '--'}
            </Typography>
          </Tooltip>
        );
        
      case 'quality':
        return renderQualityIndicator(tag.quality);
        
      case 'custom':
        // Handle custom expressions (simplified implementation)
        try {
          if (column.custom_expression) {
            // Very basic expression evaluation - in production, use a safer expression evaluator
            const expression = column.custom_expression
              .replace(/\{value\}/g, String(tag.current_value))
              .replace(/\{tag_name\}/g, tag.tag_name);
            return <Typography variant="body2">{expression}</Typography>;
          }
        } catch (error) {
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return value;
        
      default:
        return <Typography variant="body2">{String(value)}</Typography>;
    }
  };
  
  // Render alarm status with multiple alarms support
  const renderAlarmStatus = (alarms: AlarmDefinition[], onAlarmClick?: (alarm: AlarmDefinition) => void) => {
    if (!alarms || alarms.length === 0) {
      return (
        <Chip
          label="No Alarms"
          size="small"
          variant="outlined"
          sx={{ color: 'text.secondary', borderColor: 'grey.300' }}
        />
      );
    }
    
    // Group alarms by status
    const activeAlarms = alarms.filter(alarm => alarm.current_status === 'ACTIVE');
    const acknowledgedAlarms = alarms.filter(alarm => alarm.current_status === 'ACKNOWLEDGED');
    
    if (activeAlarms.length > 0) {
      // Show highest priority active alarm
      const highestPriorityAlarm = activeAlarms.reduce((prev, current) => 
        (prev.alarm_priority.level > current.alarm_priority.level) ? prev : current
      );
      
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Chip
            label={`${highestPriorityAlarm.alarm_priority.name} (${activeAlarms.length})`}
            size="small"
            sx={{ 
              backgroundColor: highestPriorityAlarm.alarm_priority.color,
              color: 'white',
              cursor: onAlarmClick ? 'pointer' : 'default'
            }}
            icon={<WarningIcon sx={{ color: 'white !important' }} />}
            onClick={() => onAlarmClick?.(highestPriorityAlarm)}
          />
          {acknowledgedAlarms.length > 0 && (
            <Chip
              label={`ACK (${acknowledgedAlarms.length})`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
        </Box>
      );
    } else if (acknowledgedAlarms.length > 0) {
      return (
        <Chip
          label={`Acknowledged (${acknowledgedAlarms.length})`}
          size="small"
          color="warning"
          variant="outlined"
        />
      );
    } else {
      return (
        <Chip
          label="Normal"
          size="small"
          color="success"
          variant="outlined"
          icon={<CheckCircleIcon />}
        />
      );
    }
  };
  
  // Render quality indicator
  const renderQualityIndicator = (quality: string) => {
    const qualityConfig = {
      'GOOD': { color: 'success', icon: <CheckCircleIcon />, label: 'Good' },
      'BAD': { color: 'error', icon: <ErrorIcon />, label: 'Bad' },
      'UNCERTAIN': { color: 'warning', icon: <WarningIcon />, label: 'Uncertain' },
    };
    
    const config = qualityConfig[quality as keyof typeof qualityConfig] || qualityConfig.UNCERTAIN;
    
    return (
      <Chip
        label={config.label}
        size="small"
        color={config.color as any}
        variant="outlined"
        icon={config.icon}
      />
    );
  };
  
  // Get cell value for a specific column
  const getCellValue = (tag: TableTag, column: TableColumn): any => {
    switch (column.type) {
      case 'tag_name':
        return tag.tag_name;
      case 'value':
        return tag.current_value;
      case 'unit':
        return tag.unit;
      case 'alarm_status':
        return tag.alarm_definitions;
      case 'description':
        return tag.description;
      case 'quality':
        return tag.quality;
      case 'custom':
        return tag.current_value; // Base value for custom expressions
      default:
        return null;
    }
  };
  
  const tableContent = (
    <TableContainer 
      component={Paper} 
      sx={{ 
        maxHeight: expanded ? maxHeight : 0,
        overflow: 'auto',
        transition: 'max-height 0.3s ease-in-out'
      }}
    >
      <Table size={size} stickyHeader>
        <TableHead>
          <TableRow>
            {showRowNumbers && (
              <TableCell 
                sx={{ 
                  backgroundColor: headerColor,
                  color: 'white',
                  fontWeight: 'bold',
                  width: 50
                }}
              >
                #
              </TableCell>
            )}
            {visibleColumns.map((column) => (
              <TableCell
                key={column.id}
                sx={{
                  backgroundColor: headerColor,
                  color: 'white',
                  fontWeight: 'bold',
                  width: column.width,
                  minWidth: column.width ? undefined : 100,
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tags.map((tag, index) => (
            <TableRow
              key={tag.id}
              sx={{
                backgroundColor: alternateRowColors && index % 2 === 1 ? 'grey.50' : 'transparent',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {showRowNumbers && (
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                  {index + 1}
                </TableCell>
              )}
              {visibleColumns.map((column) => (
                <TableCell key={column.id}>
                  {formatValue(getCellValue(tag, column), column, tag)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
  
  if (!collapsible) {
    return (
      <Paper elevation={3} sx={{ minWidth: 300 }}>
        <Box sx={{ p: 1, backgroundColor: headerColor, color: 'white' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
        </Box>
        {tableContent}
      </Paper>
    );
  }
  
  return (
    <Paper elevation={3} sx={{ minWidth: 300 }}>
      <Box 
        sx={{ 
          p: 1, 
          backgroundColor: headerColor, 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {title} ({tags.length} tags)
        </Typography>
        <IconButton size="small" sx={{ color: 'white' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        {tableContent}
      </Collapse>
    </Paper>
  );
};

export default ConfigurableTableBadge;

// Utility function to create default column configurations
export const createDefaultColumns = (): TableColumn[] => [
  {
    id: 'tag_name',
    label: 'Tag Name',
    type: 'tag_name',
    visible: true,
    width: 150,
  },
  {
    id: 'value',
    label: 'Value',
    type: 'value',
    visible: true,
    width: 100,
    format: 'number',
    decimal_places: 2,
  },
  {
    id: 'unit',
    label: 'Unit',
    type: 'unit',
    visible: true,
    width: 60,
  },
  {
    id: 'alarm_status',
    label: 'Alarm Status',
    type: 'alarm_status',
    visible: true,
    width: 120,
  },
  {
    id: 'quality',
    label: 'Quality',
    type: 'quality',
    visible: false,
    width: 80,
  },
  {
    id: 'description',
    label: 'Description',
    type: 'description',
    visible: false,
    width: 200,
  },
];

// Utility function to create table configuration for specific use cases
export const createValveTableColumns = (): TableColumn[] => [
  {
    id: 'tag_name',
    label: 'Valve',
    type: 'tag_name',
    visible: true,
    width: 120,
  },
  {
    id: 'position',
    label: 'Position',
    type: 'value',
    visible: true,
    width: 80,
    format: 'boolean',
  },
  {
    id: 'alarm_status',
    label: 'Status',
    type: 'alarm_status',
    visible: true,
    width: 100,
  },
];

export const createPumpTableColumns = (): TableColumn[] => [
  {
    id: 'tag_name',
    label: 'Pump',
    type: 'tag_name',
    visible: true,
    width: 120,
  },
  {
    id: 'running',
    label: 'Running',
    type: 'value',
    visible: true,
    width: 80,
    format: 'boolean',
  },
  {
    id: 'flow_rate',
    label: 'Flow Rate',
    type: 'custom',
    visible: true,
    width: 100,
    custom_expression: '{value} L/min',
  },
  {
    id: 'alarm_status',
    label: 'Status',
    type: 'alarm_status',
    visible: true,
    width: 100,
  },
];

export const createTemperatureTableColumns = (): TableColumn[] => [
  {
    id: 'tag_name',
    label: 'Sensor',
    type: 'tag_name',
    visible: true,
    width: 150,
  },
  {
    id: 'temperature',
    label: 'Temperature',
    type: 'value',
    visible: true,
    width: 100,
    format: 'number',
    decimal_places: 1,
  },
  {
    id: 'unit',
    label: 'Unit',
    type: 'unit',
    visible: true,
    width: 50,
  },
  {
    id: 'alarm_status',
    label: 'Status',
    type: 'alarm_status',
    visible: true,
    width: 120,
  },
];
